import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { apiPutRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';

/**
 * 生年（西暦）の設定
 */
const BirthYearPreference = (): ReactElement | null => {
    const { userInfo, refreshUserInfo } = useUser();
    const { t } = useTranslation('settings');

    const [birthYearInput, setBirthYearInput] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const [highlighted, setHighlighted] = useState<boolean>(false);

    useEffect(() => {
        if (userInfo?.birthYear) {
            setBirthYearInput(String(userInfo.birthYear));
        } else {
            setBirthYearInput('');
        }
    }, [userInfo?.birthYear]);

    useEffect(() => {
        if (window.location.hash === '#birth-year') {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(true);
        }
    }, []);

    useEffect(() => {
        if (!highlighted) return;
        const timer = window.setTimeout(() => setHighlighted(false), 2000);
        return () => window.clearTimeout(timer);
    }, [highlighted]);

    const validateInput = (value: string): { valid: boolean; birthYear: number | null } => {
        const trimmed = value.trim();
        if (trimmed === '') {
            return { valid: true, birthYear: null };
        }
        if (!/^\d{4}$/.test(trimmed)) {
            setErrorMessage(t('birthYear.invalid'));
            return { valid: false, birthYear: null };
        }
        const year = Number(trimmed);
        if (year < 1925 || year > 2025) {
            setErrorMessage(t('birthYear.invalid'));
            return { valid: false, birthYear: null };
        }
        return { valid: true, birthYear: year };
    };

    const handleSave = async () => {
        setErrorMessage('');

        const { valid, birthYear } = validateInput(birthYearInput);
        if (!valid) return;

        setIsSaving(true);
        try {
            const payload = birthYear === null ? {} : { birthYear };
            const response = await apiPutRequest('/api/settings/birth-year', payload);
            if (!response.ok) {
                setErrorMessage(t('birthYear.error'));
                return;
            }
            await refreshUserInfo();
            window.location.href = '/desk';
            return;
        } catch (error) {
            console.error(error);
            setErrorMessage(t('birthYear.error'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section id="birth-year" ref={sectionRef} className="scroll-mt-24">
            <div
                className={`flex flex-col gap-3 rounded-md border transition-colors duration-500 py-4 ${
                    highlighted ? 'border-yellow-300 bg-yellow-50 shadow-sm' : 'border-transparent'
                }`}
            >
                <h3 className="text-lg font-medium">{t('birthYear.heading')}</h3>
                <p className="text-sm text-gray-600">{t('birthYear.description')}</p>
                <div className="flex flex-wrap gap-3 items-end max-w-md">
                    <div className="flex-1 min-w-[180px]">
                        <Input
                            value={birthYearInput}
                            onChange={(event) => {
                                const normalized = event.target.value.replace(/[０-９]/g, (digit) =>
                                    String.fromCharCode(digit.charCodeAt(0) - 0xfee0),
                                );
                                const sanitized = normalized.replace(/\D/g, '').slice(0, 4);
                                setBirthYearInput(sanitized);
                                setErrorMessage('');
                            }}
                            placeholder={t('birthYear.placeholder')}
                            inputMode="numeric"
                            maxLength={4}
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="px-4 py-2 h-10 w-auto"
                        onClick={handleSave}
                        isLoading={isSaving}
                        loadingLabel={t('birthYear.saving')}
                    >
                        {t('birthYear.save')}
                    </Button>
                </div>
                {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
            </div>
        </section>
    );
};

export default BirthYearPreference;
